import axios from 'axios';

// Your APEX REST API base URL
const API_BASE_URL = 'https://oracleapex.com/ords/sbas';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============= AUTHENTICATION =============

export const loginUser = async (username, password) => {
  // Simple mock login - just validate they entered something
  // In production, replace this with your actual auth endpoint
  if (username && password) {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Check if user is admin (only DAVID has admin access)
    const isAdmin = username.toUpperCase() === 'DAVID';
    
    return {
      username: username,
      name: username,
      role: isAdmin ? 'Admin' : 'User',
      isAdmin: isAdmin,
    };
  }
  throw new Error('Invalid username or password');
};

// ============= CLIENTS API =============

export const getClients = async () => {
  let allItems = [];
  let offset = 0;
  const limit = 1000;

  while (true) {
    const response = await api.get(`/sbasclients/?limit=${limit}&offset=${offset}`);

    const items = response.data.items || [];

    if (items.length === 0) {
      break;
    }

    allItems = allItems.concat(items);
    offset += items.length;

    if (items.length < limit) {
      break;
    }
  }
  return { items: allItems };
};

export const createClient = async (clientData) => {
  const response = await api.post('/sbasclients/', clientData);
  return response.data;
};

// ============= TIME ENTRIES API =============

export const getTimeEntries = async (username, date) => {
  const encodedUsername = encodeURIComponent(username);
  const response = await api.get(`/sbastimedata/?q={"user_":"${encodedUsername}","start_date":{"$like":"${date}%"}}`);
  return response.data;
};

export const getWeekEntries = async (username, startDate) => {
  // Fetch ALL entries for user with pagination
  const encodedUsername = encodeURIComponent(username);
  let allItems = [];
  let offset = 0;
  const limit = 1000; // APEX max
  
  while (true) {
    const response = await api.get(
      `/sbastimedata/?q={"user_":"${encodedUsername}"}&limit=${limit}&offset=${offset}&_ts=${Date.now()}`
    );
    
    const items = response.data.items || [];
    
    if (items.length === 0) {
      break;
    }
    
    allItems = allItems.concat(items);
    offset += items.length;
    
    if (items.length < limit) {
      break;
    }
  }
  
  return { items: allItems };
};

export const getWeekEntriesGrouped = async (username, startDate) => {
  // Fetch ALL entries for user with pagination support
  const encodedUsername = encodeURIComponent(username);
  let allItems = [];
  let offset = 0;
  const limit = 1000; // Fetch 1000 at a time (APEX max)
  
  console.log(`Fetching all entries for user: ${username}`);
  
  while (true) {
    console.log(`Fetching page: offset=${offset}, limit=${limit}`);
    
    const response = await api.get(
      `/sbastimedata/?q={"user_":"${encodedUsername}"}&limit=${limit}&offset=${offset}&_ts=${Date.now()}`
    );
    
    const items = response.data.items || [];
    console.log(`Received ${items.length} items in this batch`);
    
    if (items.length === 0) {
      // No more items
      break;
    }
    
    allItems = allItems.concat(items);
    offset += items.length;
    
    // If we got fewer items than limit, we've reached the end
    if (items.length < limit) {
      break;
    }
  }
  
  console.log(`Total items fetched: ${allItems.length}`);
  return { items: allItems };
};

export const getLastEndTime = async (username, date) => {
  const encodedUsername = encodeURIComponent(username);
  const response = await api.get(
    `/sbastimedata/?q={"user_":"${encodedUsername}","start_date":{"$like":"${date}%"}}&orderBy=timedata_id_pk:desc&limit=1`
  );
  return response.data;
};

export const createTimeEntry = async (entryData) => {
  const response = await api.post('/sbastimedata/', entryData);
  return response.data;
};

export const updateTimeEntry = async (id, entryData) => {
  const response = await api.put(`/sbastimedata/${id}`, entryData);
  return response.data;
};

export const deleteTimeEntry = async (id) => {
  const response = await api.delete(`/sbastimedata/${id}`);
  return response.data;
};


// Get entries by date range for reports (ADMIN - ALL USERS)
export const getEntriesByDateRange = async (username, startDate, endDate) => {
  try {
    // Fetch ALL entries for ALL users (no user filter for admin)
    let allItems = [];
    let offset = 0;
    const limit = 1000;

    console.log('Fetching all entries for ALL users (admin mode)');

    while (true) {
      console.log(`Fetching page: offset=${offset}, limit=${limit}`);

      // NO USER FILTER - get everything!
      const response = await api.get(`/sbastimedata/?limit=${limit}&offset=${offset}`);

      const items = response.data.items || [];
      console.log(`Received ${items.length} items in this batch`);

      if (items.length === 0) {
        break;
      }

      allItems = allItems.concat(items);
      offset += items.length;

      if (items.length < limit) {
        break;
      }
    }

    console.log(`Total items fetched (ALL USERS): ${allItems.length}`);
    return { items: allItems };
  } catch (error) {
    console.error('Error fetching entries:', error);
    throw error;
  }
};

// ============= BACKUP API =============

// Get all time entries for backup (Admin only)
export const getAllTimeEntriesForBackup = async () => {
  console.log('🔵 [API] Fetching all time entries for backup...');

  let allItems = [];
  let offset = 0;
  const limit = 1000;

  while (true) {
    console.log(`🔵 [API] Fetching time entries page: offset=${offset}, limit=${limit}`);

    const response = await api.get(`/sbastimedata/?limit=${limit}&offset=${offset}`);

    const items = response.data.items || [];
    console.log(`🔵 [API] Received ${items.length} time entries in this batch`);

    if (items.length === 0) {
      break;
    }

    allItems = allItems.concat(items);
    offset += items.length;

    if (items.length < limit) {
      break;
    }
  }

  console.log(`✅ [API] Total time entries fetched for backup: ${allItems.length}`);
  return { items: allItems };
};

// Get all clients for backup (Admin only)
export const getAllClientsForBackup = async () => {
  console.log('🔵 [API] Fetching all clients for backup...');
  return await getClients(); // Reuse existing function
};

// ============= CLIENT REPORTS API =============

// Get entries by client and date range (for all users)
export const getEntriesByClientAndDateRange = async (clientId, startDate, endDate) => {
  console.log(`🔵 [API] Fetching entries for client ${clientId} from ${startDate} to ${endDate}`);

  let allItems = [];
  let offset = 0;
  const limit = 1000;

  while (true) {
    console.log(`🔵 [API] Fetching page: offset=${offset}, limit=${limit}`);

    // Filter by clientid
    const query = clientId ? `?q={"clientid":${clientId}}&limit=${limit}&offset=${offset}` : `?limit=${limit}&offset=${offset}`;
    const response = await api.get(`/sbastimedata/${query}`);

    const items = response.data.items || [];
    console.log(`🔵 [API] Received ${items.length} entries in this batch`);

    if (items.length === 0) {
      break;
    }

    allItems = allItems.concat(items);
    offset += items.length;

    if (items.length < limit) {
      break;
    }
  }

  console.log(`✅ [API] Total entries fetched for client: ${allItems.length}`);

  // Filter by date range on client side
  if (startDate && endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    allItems = allItems.filter(entry => {
      const entryDate = new Date(entry.start_date);
      return entryDate >= start && entryDate <= end;
    });

    console.log(`✅ [API] Filtered to ${allItems.length} entries within date range`);
  }

  return { items: allItems };
};